import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	useAIConversationFilterQuery,
} from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { reactQueryClient } from '../../../ReactQueryProvider';
import WorkingLionImageUrl from '../../../assets/aiAssistantThinking.png';
import { Avatar2 } from '../../../components/Avatar2';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { SparklesIcon } from '../../../components/svgs/icons/SparklesIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import {
	AIChatAssistantMessage,
	removeErrorMessageFromConversationCache,
	useAIAssistantStreamingMutation,
} from '../../../containers/templates/AssistantChat';
import { Button } from '../../Button';
import { Dialog, DialogBackdrop, DialogBody, DialogHeader, DialogPopup, DialogPortal, DialogTitle } from '../../Dialog';
import { Column, Row } from '../../PageLayout';
import { AIConversationMessage } from '../AIConversationMessage';
import type { TextRenderer } from '../AIConversationMessage/renderers';
import { ChatConversation, ChatConversationContent, ChatConversationScrollButton } from '../ChatConversation';
import { ChatInput } from '../ChatInput';
import {
	PromptInput,
	PromptInputAttachmentButton,
	PromptInputBody,
	PromptInputFooter,
	type PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../PromptInput';
import {
	AI_CHAT_FILE_ACCEPT,
	AI_CHAT_MAX_FILE_SIZE,
	AI_CHAT_MAX_FILES,
	PromptInputAttachments,
	useAIChatAttachments,
} from '../PromptInput/PromptInputAttachments';
import { AI_THINKING_MESSAGES, ThinkingIndicator } from '../ThinkingIndicator';
import { s } from './styles';

interface AIAssistantDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	impersonationContext?: Api.IImpersonationContext;
	assistantName: string;
	conversationType: Api.AIConversationType;
	title?: string;
	initialMessage?: string;
	placeholder?: string;
	agentTemplateId?: string;
	customInitialAction?: Api.AIAction;
	/**
	 * When true, the first stream of a new conversation sets `autoStart: true`
	 * so the BE-side agent injects its configured starter prompt. Mutually
	 * exclusive with `customInitialAction`.
	 */
	autoStart?: boolean;
	/** When true, starts a new conversation instead of reusing an existing one */
	startNewConversation?: boolean;
	dismissOnOutsideClick?: boolean;
	/** Custom text renderers for transforming message content (e.g., HexColorRenderer) */
	customRenderers?: TextRenderer[];
	/** Custom messages to rotate through while AI is thinking */
	thinkingMessages?: string[];
	supportsAttachments?: boolean;
}

const DEFAULT_TITLE = 'Ask Lev';
const DEFAULT_INITIAL_MESSAGE =
	"Hey! I'm here to help you with any questions you have. Feel free to ask me anything about your account, features, or general guidance.";
const DEFAULT_PLACEHOLDER = 'Ask me anything...';

const AIAssistantDialogLayout = ({ children, title }: { children: React.ReactNode; title: string }) => {
	return (
		<>
			<DialogHeader styles={[s.header]}>
				<div className={css(s.headerStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon className={css(s.sparklesIcon)} />
					<DialogTitle styles={[s.headerTitle]}>{title}</DialogTitle>
				</div>
			</DialogHeader>
			<DialogBody styles={[s.body]}>{children}</DialogBody>
		</>
	);
};

export const useAIAssistantConversation = (
	isOpen: boolean,
	assistantName: string,
	conversationType: Api.AIConversationType,
	impersonationContext?: Api.IImpersonationContext,
	agentTemplateId?: string,
	startNewConversation?: boolean
) => {
	const logger = useEventLogging('useAIAssistantConversation');

	// Try to find an existing conversation of this type
	// Skip this query when starting a new conversation or testing a specific agent template
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: conversationType,
				},
			],
		},
		impersonationContext,
		enabled: !startNewConversation && !agentTemplateId,
	});

	// If no conversation exists, create one
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useAIAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
	});

	// Get the conversation ID (from existing or newly created)
	const conversationId = React.useMemo(() => {
		// When testing a specific agent template or forcing new conversation, only use the newly created conversation
		if (agentTemplateId || startNewConversation) {
			return aiAssistantCreateConversationMutation.data?.id;
		}
		const existingConversation = aiConversationFilterQuery.data?.[0];
		if (existingConversation) {
			return existingConversation.id;
		}
		return aiAssistantCreateConversationMutation.data?.id;
	}, [
		aiConversationFilterQuery.data,
		aiAssistantCreateConversationMutation.data,
		agentTemplateId,
		startNewConversation,
	]);

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(conversationId),
		id: conversationId,
		impersonationContext,
		staleTime: Infinity,
	});

	// Create conversation if needed
	React.useEffect(() => {
		// RACE CONDITION FIX (#17636):
		if (!agentTemplateId && !startNewConversation) {
			return;
		}

		const shouldCreateNew = aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data;

		if (isOpen && shouldCreateNew) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
				agentTemplateId,
			});
		}
	}, [
		aiAssistantCreateConversationMutation,
		isOpen,
		impersonationContext,
		assistantName,
		agentTemplateId,
		startNewConversation,
	]);

	return {
		conversationId,
		aiConversationChatMessageQuery,
		isLoadingConversation:
			(!agentTemplateId && !startNewConversation && aiConversationFilterQuery.isLoading) ||
			aiAssistantCreateConversationMutation.isPending,
	};
};

export function clearConversationCache(conversationId: string, impersonationContext?: Api.IImpersonationContext) {
	const conversationQueryKey = getConversationChatMessageQueryKey({
		id: conversationId,
		impersonationContext,
	});
	const messages: Api.AIChatMessage[] = reactQueryClient.getQueryData(conversationQueryKey) ?? [];
	if (messages?.length) {
		reactQueryClient.setQueryData(conversationQueryKey, []);
	}
}

const AIAssistantDialogContent = ({
	open,
	impersonationContext,
	assistantName,
	conversationType,
	title = DEFAULT_TITLE,
	initialMessage,
	placeholder = DEFAULT_PLACEHOLDER,
	agentTemplateId,
	customInitialAction,
	autoStart,
	startNewConversation,
	customRenderers,
	thinkingMessages,
	supportsAttachments = false,
}: AIAssistantDialogProps) => {
	// Suppress the default welcome message when the agent is auto-starting,
	// since the agent will produce the first message itself.
	const effectiveInitialMessage =
		initialMessage !== undefined ? initialMessage : startNewConversation && autoStart ? '' : DEFAULT_INITIAL_MESSAGE;
	const id = React.useId();
	const queryClient = useQueryClient();
	const logger = useEventLogging('AIAssistantDialog');
	const errorMessages = useErrorMessages();

	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: data => {
			if (
				data.conversationType === Api.AIConversationType.AccountOnboarding &&
				!aiAssistantStreamingMutation.isLoading
			) {
				if (autoStart) {
					aiAssistantStreamingMutation.mutate({
						conversationId: data.id,
						chatRequest: {
							_type: 'AIMessageRequest',
							autoStart: true,
						} as Api.AIMessageRequest,
						impersonationContext,
					});
				} else {
					aiAssistantStreamingMutation.mutate({
						conversationId: data.id,
						chatRequest: {
							_type: 'AIActionRequest',
							action: customInitialAction || {
								_type: 'ContentSettingsIntakeAction',
							},
						} as Api.AIActionRequest,
						impersonationContext,
					});
				}
			}
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const { conversationId, aiConversationChatMessageQuery, isLoadingConversation } = useAIAssistantConversation(
		open,
		assistantName,
		conversationType,
		impersonationContext,
		agentTemplateId,
		startNewConversation
	);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName,
	});

	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;
	const attachmentTextareaRef = React.useRef<HTMLTextAreaElement>(null);

	React.useEffect(() => {
		// Skip creating universal assistant conversation when:
		// - testing a specific agent template (useAIAssistantConversation handles that case)
		// - startNewConversation is true (useAIAssistantConversation handles that case)
		if (!agentTemplateId && !startNewConversation) {
			createUniversalAssistantConversation({ assistantName, impersonationContext });
		}
		return () => {
			clearConversationCache(conversationId, impersonationContext);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [impersonationContext, agentTemplateId, startNewConversation]);

	// Fire an `autoStart` stream when a new conversation is ready — the BE
	// agent then injects its starter prompt and produces the first response.
	const hasTriggeredAutoStart = React.useRef(false);
	React.useEffect(() => {
		if (
			startNewConversation &&
			autoStart &&
			conversationId &&
			!hasTriggeredAutoStart.current &&
			!aiAssistantStreamingMutation.isLoading
		) {
			hasTriggeredAutoStart.current = true;
			aiAssistantStreamingMutation.mutate({
				conversationId,
				chatRequest: {
					_type: 'AIMessageRequest',
					autoStart: true,
				} as Api.AIMessageRequest,
				impersonationContext,
			});
		}
	}, [startNewConversation, autoStart, conversationId, aiAssistantStreamingMutation, impersonationContext]);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) {
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmit = async (prompt: string) => {
		if (!conversationId) {
			toast.error('Conversation not ready. Please try again.');
			return;
		}

		try {
			removeErrorMessageFromConversationCache(conversationId, impersonationContext);

			// Add user message to cache
			queryClient.setQueryData(
				getConversationChatMessageQueryKey({
					id: conversationId,
					impersonationContext,
				}),
				(prevMessages: Api.AIChatMessage[]) => [
					...(prevMessages ?? []),
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				]
			);

			// Send to API
			const chatRequest: Api.AIMessageRequest = {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			};

			aiAssistantStreamingMutation.mutate({
				conversationId,
				chatRequest,
				impersonationContext,
			});
		} catch (error) {
			logger.logApiError('AIAssistantDialog-submit-error', error as Api.IOperationResultNoValue);
			toast.error('Failed to send message. Please try again.');
		}
	};

	const handleAttachmentPromptSubmit = ({ text }: PromptInputMessage) => {
		if (!conversationId) {
			toast.error('Conversation not ready. Please try again.');
			return;
		}
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}

		try {
			removeErrorMessageFromConversationCache(conversationId, impersonationContext);

			const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(attachment => ({
				_type: 'AIFileContent',
				fileAttachmentId: attachment.id,
			}));
			const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];

			queryClient.setQueryData(
				getConversationChatMessageQueryKey({
					id: conversationId,
					impersonationContext,
				}),
				(prevMessages: Api.AIChatMessage[]) => [
					...(prevMessages ?? []),
					{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
				]
			);

			aiAssistantStreamingMutation.mutate({
				conversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							contents,
							id: null,
						},
					],
				} as Api.AIMessageRequest,
				impersonationContext,
			});

			resetAttachments();
		} catch (error) {
			logger.logApiError('AIAssistantDialog-submit-error', error as Api.IOperationResultNoValue);
			toast.error('Failed to send message. Please try again.');
		}
	};

	if (isLoadingConversation || !conversationId) {
		return (
			<AIAssistantDialogLayout title={title}>
				<Row fullHeight center>
					<LoadingSpinner />
				</Row>
			</AIAssistantDialogLayout>
		);
	}

	return (
		<AIAssistantDialogLayout title={title}>
			<ChatConversation styles={[s.conversation]}>
				<ChatConversationContent styles={[s.conversationContent]}>
					<div className={css(s.spacer)} />
					{effectiveInitialMessage && (
						<AIChatAssistantMessage>
							<span dangerouslySetInnerHTML={{ __html: effectiveInitialMessage }} />
						</AIChatAssistantMessage>
					)}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-assistant-message-${message.id ?? i}`}
								message={message}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
								customRenderers={customRenderers}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading && (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={thinkingMessages ?? AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					)}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				{supportsAttachments ? (
					<PromptInput
						onSubmit={handleAttachmentPromptSubmit}
						accept={AI_CHAT_FILE_ACCEPT}
						maxFiles={AI_CHAT_MAX_FILES}
						maxFileSize={AI_CHAT_MAX_FILE_SIZE}
						onError={err => toast.error(err.message)}
						multiple
					>
						<PromptInputAttachments controller={attachmentsController} />
						<PromptInputBody>
							<PromptInputTextarea ref={attachmentTextareaRef} placeholder={placeholder} />
						</PromptInputBody>
						<PromptInputFooter>
							<PromptInputTools>
								<PromptInputAttachmentButton />
								<PromptInputSpeechButton textareaRef={attachmentTextareaRef} />
							</PromptInputTools>
							<PromptInputSubmit status={aiAssistantStreamingMutation.status} disabled={isUploading} />
						</PromptInputFooter>
					</PromptInput>
				) : (
					<ChatInput
						streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
						placeholder={placeholder}
						onSubmit={handlePromptSubmit}
						supportsImages={false}
					/>
				)}
			</div>
		</AIAssistantDialogLayout>
	);
};

const AIAssistantDialogErrorBoundary = ({ children }: { children: React.ReactNode }) => {
	const logger = useEventLogging('AIAssistantDialogErrorBoundary');

	return (
		<ErrorBoundary
			fallback={
				<Column fullHeight center styles={[s.errorBoundaryDisplay]}>
					<WarningIcon />
					<strong className={css(s.errorMessage)}>Error occurred loading assistant</strong>
					<Button variant='secondary' onClick={() => window.location.reload()}>
						Reload Page
					</Button>
				</Column>
			}
			onError={(error: Error) => {
				logger.logApiError('AIAssistantDialogErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export const AIAssistantDialog = (props: AIAssistantDialogProps) => {
	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			props.onOpenChange(open);
		},
		[props]
	);

	return (
		<Dialog open={props.open} onOpenChange={handleOpenChange} dismissOnOutsideClick={props.dismissOnOutsideClick}>
			<DialogPortal>
				<DialogBackdrop />
				<DialogPopup showCloseButton={true} variant='default' styles={[s.popup]}>
					<AIAssistantDialogErrorBoundary>
						<AIAssistantDialogContent {...props} />
					</AIAssistantDialogErrorBoundary>
				</DialogPopup>
			</DialogPortal>
		</Dialog>
	);
};
